import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff, X, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { ServiceIcon } from "@/components/blocks";

const ICON_OPTIONS = ["Baby", "HeartPulse", "Heart", "Flower2", "ShieldCheck", "Sun", "Stethoscope", "Sparkles"];

const empty = { title: "", description: "", icon: "Stethoscope", published: true, order: 0 };

export default function AdminServices() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => { setLoading(true); api.get("/admin/services").then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(load, []);

  const openNew = () => { setForm(empty); setEditing("new"); };
  const openEdit = (s) => { setForm({ title: s.title, description: s.description, icon: s.icon, published: s.published, order: s.order || 0 }); setEditing(s.id); };

  const save = async () => {
    if (!form.title.trim() || !form.description.trim()) { toast.error("Title and description are required."); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/services", form);
      else await api.put(`/admin/services/${editing}`, form);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePublish = async (s) => {
    try { await api.put(`/admin/services/${s.id}`, { ...s, published: !s.published }); load(); }
    catch (e) { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try { await api.delete(`/admin/services/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Delete failed"); }
  };

  const inputCls = "w-full rounded-xl border border-soft-border bg-white p-3 text-sm outline-none focus:border-terracotta";

  return (
    <div data-testid="admin-services-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-charcoal">Services</h1>
          <p className="mt-1 text-sm text-taupe">Manage the service categories shown on the website.</p>
        </div>
        <button data-testid="service-add-btn" onClick={openNew} className="inline-flex items-center gap-2 rounded-full bg-terracotta px-5 py-3 text-sm font-semibold text-cream hover:bg-terracotta-dark"><Plus className="h-4 w-4" /> Add Service</button>
      </div>

      {loading ? <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div> : (
        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((s) => (
            <div key={s.id} data-testid="service-card" className="rounded-2xl border border-soft-border bg-cream p-6">
              <div className="flex items-center justify-between">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-blush text-terracotta"><ServiceIcon name={s.icon} className="h-5 w-5" /></span>
                <span className={`text-xs font-semibold ${s.published ? "text-green-600" : "text-taupe"}`}>{s.published ? "Published" : "Hidden"}</span>
              </div>
              <h3 className="mt-4 font-serif text-lg text-charcoal">{s.title}</h3>
              <p className="mt-2 line-clamp-3 text-sm text-taupe">{s.description}</p>
              <div className="mt-4 flex items-center gap-1">
                <button onClick={() => openEdit(s)} className="rounded-lg p-2 text-terracotta hover:bg-blush" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => togglePublish(s)} className="rounded-lg p-2 text-taupe hover:bg-blush" aria-label="Toggle publish">{s.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                <button onClick={() => remove(s.id)} className="rounded-lg p-2 text-destructive hover:bg-rose-50" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-charcoal/40 p-4" onClick={() => setEditing(null)}>
          <div data-testid="service-modal" className="w-full max-w-lg rounded-3xl bg-background p-6 shadow-lift md:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl text-charcoal">{editing === "new" ? "Add Service" : "Edit Service"}</h2>
              <button onClick={() => setEditing(null)} className="rounded-lg p-2 text-taupe hover:bg-blush"><X className="h-5 w-5" /></button>
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-charcoal">Title</label>
                <input data-testid="service-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-charcoal">Description</label>
                <textarea data-testid="service-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-charcoal">Icon</label>
                  <select data-testid="service-icon" value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className={inputCls}>
                    {ICON_OPTIONS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-charcoal">Order</label>
                  <input type="number" data-testid="service-order" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className={inputCls} />
                </div>
              </div>
              <label className="flex items-center gap-3">
                <input type="checkbox" data-testid="service-published" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="h-5 w-5 accent-terracotta" />
                <span className="text-sm text-charcoal">Published (visible on website)</span>
              </label>
            </div>
            <button data-testid="service-save" onClick={save} disabled={saving} className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-terracotta px-6 py-3.5 text-sm font-semibold text-cream hover:bg-terracotta-dark disabled:opacity-70">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save Service
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
